import React from "react";
import { Field, FieldRenderProps } from "react-final-form";
import { Button, Col, Label, Row, Toast, ToastBody, ToastHeader } from "reactstrap";
import { formatEuro, nameof } from "../../app/Utils";
import { CashDeskSimulatorProps } from "../CashDeskSimulator";
import { ReceiptDetailDto } from "../../../store/models/CustomerInteractionDto";

function getRatingIcon(val: number, rating: number): string {
  if (rating - val >= 0) return "bi-star-fill";
  if (rating - val >= -0.59) return "bi-star-half";
  return "bi-star";
}

interface ProductItemProps {
  example: ReceiptDetailDto;
  imageFolder: string;
  badgeIcon: string;
  badgeAlt: string;
  badgeTitle: string;
  isSelected: boolean;
  onToggle: () => void;
  colStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  toastStyle?: React.CSSProperties;
  toastHeaderStyle?: React.CSSProperties;
}

const ProductItem = ({
  example,
  imageFolder,
  badgeIcon,
  badgeAlt,
  badgeTitle,
  isSelected,
  onToggle,
  colStyle,
  buttonStyle,
  toastStyle,
  toastHeaderStyle,
}: ProductItemProps) => (
  <Col md={4} style={colStyle}>
    <Button
      color="link"
      onClick={onToggle}
      style={{
        textDecoration: "none",
        color: "inherit",
        ...buttonStyle,
      }}
    >
      <Toast
        className={isSelected ? "bg-info" : ""}
        transition={{ timeout: 150 }}
        style={{
          textAlign: "center",
          cursor: "hand",
          ...toastStyle,
        }}
      >
        <ToastHeader style={toastHeaderStyle}>
          {example.itemInfo.organic && (
            <img
              src={badgeIcon}
              alt={badgeAlt}
              title={badgeTitle}
              style={{ height: "21px", paddingRight: "10px" }}
            />
          )}
          {example.itemInfo.supplierItemText}
        </ToastHeader>
        <ToastBody style={{ padding: 0 }}>
          <img
            alt={example.itemInfo.supplierItemText}
            src={`/${imageFolder}/${example.itemInfo.refNumber}.png`}
            style={{ width: "100%" }}
          />
          {[1, 2, 3, 4, 5].map((val) => (
            <i
              key={val}
              className={getRatingIcon(val, example.itemInfo.rating)}
              style={{ paddingRight: 8 }}
            />
          ))}{" "}
          ({example.itemInfo.votings})
          <big style={{ paddingLeft: "128px" }}>
            <b>{formatEuro(example.salesPrice)}</b>
          </big>
        </ToastBody>
      </Toast>
    </Button>
  </Col>
);

interface StyleProps {
  colStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  toastStyle?: React.CSSProperties;
  toastHeaderStyle?: React.CSSProperties;
}

interface ProductListProps extends StyleProps {
  examples: ReceiptDetailDto[];
  imageFolder: string;
  badgeIcon: string;
  badgeAlt: string;
  badgeTitle: string;
  fieldProps: FieldRenderProps<string[], HTMLElement>;
}

const toggleProduct = (fieldProps: FieldRenderProps<string[], HTMLElement>, refNumber: string) => {
  fieldProps.input.onChange(
    fieldProps.input.value.includes(refNumber)
      ? fieldProps.input.value.filter((id: string) => id !== refNumber)
      : [...fieldProps.input.value, refNumber]
  );
};

const ProductList = ({
  examples,
  imageFolder,
  badgeIcon,
  badgeAlt,
  badgeTitle,
  fieldProps,
  colStyle,
  buttonStyle,
  toastStyle,
  toastHeaderStyle,
}: ProductListProps) => (
  <>
    {examples.map((example) => (
      <ProductItem
        key={example.itemInfo.refNumber}
        example={example}
        imageFolder={imageFolder}
        badgeIcon={badgeIcon}
        badgeAlt={badgeAlt}
        badgeTitle={badgeTitle}
        isSelected={fieldProps.input.value.includes(example.itemInfo.refNumber)}
        onToggle={() => toggleProduct(fieldProps, example.itemInfo.refNumber)}
        colStyle={colStyle}
        buttonStyle={buttonStyle}
        toastStyle={toastStyle}
        toastHeaderStyle={toastHeaderStyle}
      />
    ))}
  </>
);

interface ReceiptDetailPanelVariantProps extends StyleProps {
  examples: ReceiptDetailDto[];
  imageFolder: string;
  badgeIcon: string;
  badgeAlt: string;
  badgeTitle: string;
  labelColMd?: number;
}

export const ReceiptDetailPanelVariant = ({
  examples,
  imageFolder,
  badgeIcon,
  badgeAlt,
  badgeTitle,
  labelColMd = 9,
  colStyle,
  buttonStyle,
  toastStyle,
  toastHeaderStyle,
}: ReceiptDetailPanelVariantProps) => (
  <>
    <Row>
      <Col md={labelColMd}>
        <Label for={nameof<CashDeskSimulatorProps>("receiptDetailIds")}>Produkte wählen</Label>
      </Col>
    </Row>
    <Row>
      <Field name={nameof<CashDeskSimulatorProps>("receiptDetailIds")} type="field">
        {(fieldProps) => (
          <ProductList
            examples={examples}
            imageFolder={imageFolder}
            badgeIcon={badgeIcon}
            badgeAlt={badgeAlt}
            badgeTitle={badgeTitle}
            fieldProps={fieldProps}
            colStyle={colStyle}
            buttonStyle={buttonStyle}
            toastStyle={toastStyle}
            toastHeaderStyle={toastHeaderStyle}
          />
        )}
      </Field>
    </Row>
  </>
);
